import { MdPerson, MdEmail, MdPhone, MdBadge, MdVerified, MdTag, MdStar } from 'react-icons/md';
import { t } from '../utils/i18n';

function formatAddress(addr) {
  if (!addr || typeof addr !== 'object') return null;
  const parts = [addr.street, addr.city, addr.postalCode, addr.country].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

export default function UserAccountSummary({ user, className = '' }) {
  if (!user) return null;

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || '—';
  const roleLabel =
    user.role === 'parent'
      ? (t('profile.parent') || 'Roditelj')
      : user.role === 'babysitter'
        ? (t('profile.babysitter') || 'Bebisiterka')
        : user.role === 'platform_admin' || user.role === 'admin'
          ? 'Admin'
          : user.role || '—';
  const addrStr = formatAddress(user.address);
  const premiumUntil = user.premiumUntil || user.premium_until;
  let premiumUntilStr = null;
  if (premiumUntil) {
    try {
      const d = new Date(premiumUntil);
      if (!Number.isNaN(d.getTime())) premiumUntilStr = d.toLocaleDateString('sr-RS', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (_) {}
  }

  const fields = [
    { icon: MdPerson, label: t('account.fullName') || 'Ime i prezime', value: fullName },
    { icon: MdEmail, label: t('account.email') || 'Email', value: user.email || '—' },
    { icon: MdPhone, label: t('account.phone') || 'Telefon', value: user.phone || (t('account.notSet') || 'Nije uneto') },
    { icon: MdBadge, label: t('account.role') || 'Uloga', value: roleLabel },
    {
      icon: MdStar,
      label: t('account.premium') || 'Pretplata (poruke)',
      value: user.hasPremium
        ? (premiumUntilStr ? `${t('profile.active') || 'Aktivan'} · ${t('account.until') || 'do'} ${premiumUntilStr}` : (t('profile.active') || 'Aktivan'))
        : (t('profile.premiumInactive') || 'Nije aktivirana'),
      accent: user.hasPremium ? 'emerald' : 'amber',
    },
  ];

  if (addrStr) {
    fields.push({ icon: MdPerson, label: t('account.address') || 'Adresa', value: addrStr });
  }

  fields.push(
    {
      icon: MdVerified,
      label: t('account.profile') || 'Profil',
      value: user.profileComplete !== false ? (t('account.profileComplete') || 'Popunjen') : (t('account.profileIncomplete') || 'Nepotpun'),
    },
    {
      icon: MdTag,
      label: t('account.memberId') || 'ID naloga',
      value: user.id != null ? String(user.id) : '—',
    }
  );

  const accentRing = {
    emerald: 'ring-emerald-500/20 dark:ring-emerald-400/15',
    amber: 'ring-amber-500/20 dark:ring-amber-400/15',
    default: 'ring-primary-500/15 dark:ring-primary-400/10',
  };

  return (
    <section
      className={`relative overflow-hidden rounded-2xl border border-gray-200/90 dark:border-gray-700/90 bg-white dark:bg-gray-900 shadow-sm ${className}`.trim()}
    >
      <div className="h-1.5 w-full bg-gradient-to-r from-primary-500 via-primary-400 to-pink-400 dark:from-primary-600 dark:via-primary-500 dark:to-pink-500" aria-hidden />
      <div className="p-5 sm:p-6 md:p-7">
        <div className="flex items-start justify-between gap-4 mb-5 md:mb-6">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-50 tracking-tight">
              {t('account.title') || 'Podaci naloga'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('account.subtitle') || 'Pregled podataka vezanih za vaš nalog.'}
            </p>
          </div>
          <div className="hidden sm:flex h-12 w-12 rounded-2xl bg-gradient-to-br from-primary-500/15 to-pink-500/10 dark:from-primary-400/20 dark:to-pink-500/15 items-center justify-center shrink-0 border border-primary-200/50 dark:border-primary-500/20">
            <MdPerson className="text-2xl text-primary-600 dark:text-primary-300" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {fields.map(({ icon: Icon, label, value, accent }, i) => (
            <div
              key={i}
              className={`group rounded-xl border border-gray-100 dark:border-gray-700/80 bg-gray-50/80 dark:bg-gray-800/40 p-4 ring-1 ${accent ? accentRing[accent] || accentRing.default : accentRing.default} transition-shadow hover:shadow-md/50 dark:hover:shadow-none`}
            >
              <div className="flex gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white dark:bg-gray-900/80 border border-gray-200/80 dark:border-gray-600/60 shadow-sm">
                  <Icon className="text-lg text-primary-600 dark:text-primary-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                    {label}
                  </p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-words leading-snug">
                    {value}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
